import { Col, Row } from 'antd';
import { CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import reportActions from 'features/report/services/actions';
import reportSelectors from 'features/report/services/selectors';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { ReportType } from '../constants';
import AppointmentChart from './components/AppointmentChart';
import IncomeCategoryChart from './components/IncomeCategoryChart';
import IncomeChart from './components/IncomeChart';
import PaymentTypesChart from './components/PaymentTypesChart';
import RevenueIncomeHeader from './components/RevenueIncomeHeader';
import StarRatingChart from './components/StartRatingChart';

const RevenueIncomeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const appointmentChartData = reportSelectors.getSummaryAppointmentChart();
  const saleChartData = reportSelectors.getSummarySaleChart();
  const categoryChartData = reportSelectors.getSummaryCategoryChart();
  const paymentTypeChartData = reportSelectors.getSummaryPaymentTypeChart();
  const reviewReportSummary = reportSelectors.getReviewReportSummary();
  const dataAppointment = reportSelectors.getParams()?.[ReportType.REVENUE];
  const viewType = reportSelectors.getViewType()?.[ReportType.REVENUE];
  const isYearComparasion = useMemo(
    () => viewType == CALENDAR_VIEW_TYPE.YEAR_COMPARISON,
    [viewType]
  );
  useEffect(() => {
    const type = (() => {
      switch (viewType) {
        case CALENDAR_VIEW_TYPE.WEEK_VIEW:
          return 'WEEK';
        case CALENDAR_VIEW_TYPE.YEAR_VIEW:
          return 'YEAR';
        case CALENDAR_VIEW_TYPE.YEAR_COMPARISON:
          return 'YEAR_COMPARE';
        case CALENDAR_VIEW_TYPE.DAY_VIEW:
        case CALENDAR_VIEW_TYPE.MONTH_VIEW:
        default:
          return 'DAY';
      }
    })();
    dispatch(
      reportActions.getSummaryAppointmentChart.fetch({
        type,
        startDate: dataAppointment?.startTime || moment().format(DATE_FORMAT),
        endDate: dataAppointment?.endTime || moment().format(DATE_FORMAT),
      })
    );
    dispatch(
      reportActions.getSummarySaleChart.fetch({
        type,
        startDate: dataAppointment?.startTime || moment().format(DATE_FORMAT),
        endDate: dataAppointment?.endTime || moment().format(DATE_FORMAT),
      })
    );
  }, [viewType, dataAppointment]);

  useEffect(() => {
    dispatch(
      reportActions.getSummaryCategoryChart.fetch({
        startDate: dataAppointment?.startTime || moment().format(DATE_FORMAT),
        endDate: dataAppointment?.endTime || moment().format(DATE_FORMAT),
      })
    );
    dispatch(
      reportActions.getSummaryPaymentTypeChart.fetch({
        startDate: dataAppointment?.startTime || moment().format(DATE_FORMAT),
        endDate: dataAppointment?.endTime || moment().format(DATE_FORMAT),
      })
    );
    dispatch(
      reportActions.getReviewReportSummary.fetch({
        startDate: dataAppointment?.startTime || moment().format(DATE_FORMAT),
        endDate: dataAppointment?.endTime || moment().format(DATE_FORMAT),
      })
    );
  }, [dataAppointment]);

  return (
    <DashboardContainer>
      <RevenueIncomeHeader />

      <DashboardContent>
        <Row style={{ height: '100%' }}>
          <Col
            span={19}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
            }}
          >
            <Row style={{ height: 450, display: 'flex', flexWrap: 'nowrap' }}>
              <CardBox
                style={{
                  width: 'calc(100% - 600px)',
                  height: 'calc(100% - 48px)',
                }}
              >
                <AppointmentChart
                  data={appointmentChartData.data}
                  bookOnline={appointmentChartData.totalOnline}
                  localAppointments={appointmentChartData.totalLocal}
                  isYearComparasion={isYearComparasion}
                  year={moment(dataAppointment?.startTime, DATE_FORMAT).get(
                    'year'
                  )}
                />
              </CardBox>
              <CardBox
                style={{
                  height: 'calc(100% - 48px)',
                  maxWidth: 600,
                }}
              >
                <IncomeCategoryChart data={categoryChartData} />
              </CardBox>
            </Row>
            <CardBox>
              <IncomeChart
                data={saleChartData}
                isYearComparasion={isYearComparasion}
                year={moment(dataAppointment?.startTime, DATE_FORMAT).get(
                  'year'
                )}
              />
            </CardBox>
          </Col>

          <Col
            xs={24}
            md={5}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
            }}
          >
            <CardBox>
              <PaymentTypesChart data={paymentTypeChartData} />
            </CardBox>
            <CardBox style={{ maxHeight: 450 }}>
              <StarRatingChart data={reviewReportSummary} />
            </CardBox>
          </Col>
        </Row>
      </DashboardContent>
    </DashboardContainer>
  );
};

export default RevenueIncomeDashboard;

const DashboardContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  height: calc(100vh - 108px);
  background-color: #f5f5f5;
  padding: 16px;
  align-items: stretch;
  justify-self: stretch;
`;

const DashboardContent = styled.div`
  margin: 0px -8px;
  height: 100%;
`;

const CardBox = styled.div`
  background-color: white;
  padding: 18px;
  margin: 6px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: calc(100% - 48px);
  align-self: stretch;
  flex: 1;
`;
